import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TicketState } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useDraft, clearLandedDraft } from '../../lib/useDraft';
import { draftKey } from '../../lib/drafts';
import { Button } from '../ui/Button';
import { SectionCard } from '../ui/Detail';
import {
  StaleNotice,
  awaitingServer,
  mutationState,
  refreshFailed,
  refreshUntilShown,
  shownState,
} from '../ui/QueryStates';
import { CHECKING_LABEL, useQueueRead, useUnsentWrite } from '../ui/useConnection';
import { useTapOnce } from '../ui/useTapOnce';
import { PhotoGrid } from '../photos/PhotoGrid';
import { TicketStatusBadge } from '../tickets/TicketBits';
import { MessageList } from '../tickets/Messages';
import { sendOnce } from '../tickets/sendOnce';
import { LANDED_MARK, LANDING_HIGHLIGHT_MS, LANDING_SCROLL } from '../ui/landing';

/** A ticket's place on the visit page: what the bell's link names (`#ticket-<id>`). */
export const ticketAnchor = (ticketId: string) => `ticket-${ticketId}`;

/** The ticket id a visit page's address names (`#ticket-<id>`), if it names one. */
export function linkedTicketOf(hash: string): string | undefined {
  return /^ticket-([0-9a-f-]+)$/i.exec(hash)?.[1];
}

export { LANDING_HIGHLIGHT_MS, LANDING_SCROLL };

/**
 * "Tickets on this job" (ops insights Phase 5a, ticket 03): the problems raised on the job the
 * tech is standing at, by them or put there by the office, each with its conversation, its
 * photos and "Add a message". The server shows a tech only the tickets of jobs they are on
 * (`ticket.forJob`, row scoped), so this card asks for this job and draws what comes back.
 * A tech writes messages and photos here, nothing else (Giuliano, 2026-10-06): status, facts
 * and resolving are the office's.
 *
 * ⭐ The bell's link lands here (UX baseline: a link lands on the exact item). `linkedTicketId`
 * is the ticket the address names; once its thread is drawn (after this card's read), it is
 * opened if it started closed, scrolled into view and marked (`aria-current` and the tint, as
 * the Tickets page marks the ticket it opened) for `LANDING_HIGHLIGHT_MS`. ONCE per link: a
 * refetch never pulls the page back to it while the tech is reading elsewhere.
 */
export function JobTicketsCard({
  jobId,
  userId,
  tz,
  linkedTicketId,
}: {
  jobId: string;
  userId: string;
  tz: string;
  linkedTicketId?: string;
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.ticket.forJob.queryOptions({ jobId }));
  const state = shownState(q, q.data);
  const tickets = q.data ?? [];

  const [lit, setLit] = useState<string | undefined>(undefined);
  const landedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!linkedTicketId) {
      landedFor.current = undefined;
      return;
    }
    if (
      landedFor.current === linkedTicketId ||
      typeof document.querySelector !== 'function'
    )
      return;
    // `linkedTicketOf` lets only an id's characters through, so the selector is well formed.
    const el = document.querySelector(`[id="${ticketAnchor(linkedTicketId)}"]`);
    if (!el) return; // not drawn yet: this runs again when the read answers
    landedFor.current = linkedTicketId;
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView(LANDING_SCROLL);
    setLit(linkedTicketId);
  }, [linkedTicketId, q.data]);
  useEffect(() => {
    if (lit === undefined) return;
    const id = setTimeout(() => setLit(undefined), LANDING_HIGHLIGHT_MS);
    return () => clearTimeout(id);
  }, [lit]);

  return (
    <SectionCard
      title={
        tickets.length ? `Tickets on this job (${tickets.length})` : 'Tickets on this job'
      }
    >
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {state === 'loading' ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : state === 'offline' ? (
        <p className="text-sm text-muted-foreground">
          You’re offline, and this job’s tickets weren’t saved on this phone.
        </p>
      ) : state === 'error' ? (
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn’t load this job’s tickets.{' '}
          <button type="button" className="underline" onClick={() => void q.refetch()}>
            Try again
          </button>
        </p>
      ) : tickets.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing flagged on this job.</p>
      ) : (
        <ul className="space-y-4">
          {tickets.map((t) => (
            <TicketThread
              key={t.id}
              ticket={t}
              userId={userId}
              tz={tz}
              linked={t.id === linkedTicketId}
              lit={t.id === lit}
            />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/**
 * One ticket: its title and status always, and its conversation, photos and "Add a message"
 * when open. A resolved ticket starts closed: what is settled is not the tech's to read first,
 * and its messages and photos are not fetched until it is opened (one read per open ticket,
 * not per ticket the job ever had). A link to it (`linked`) opens it, once per link: closing it
 * again is the tech's to do.
 */
function TicketThread({
  ticket,
  userId,
  tz,
  linked,
  lit,
}: {
  ticket: { id: string; number: number; title: string; status: TicketState };
  userId: string;
  tz: string;
  linked: boolean;
  lit: boolean;
}) {
  const [open, setOpen] = useState(ticket.status !== 'resolved' || linked);
  const [wasLinked, setWasLinked] = useState(linked);
  if (linked !== wasLinked) {
    setWasLinked(linked);
    if (linked) setOpen(true);
  }
  const bodyId = `${ticketAnchor(ticket.id)}-thread`;
  return (
    <li
      id={ticketAnchor(ticket.id)}
      aria-current={lit ? 'true' : undefined}
      className={`scroll-mt-[var(--sw-anchor-offset)] space-y-2 border-t border-border pt-3 transition-colors first:border-t-0 first:pt-0 ${
        lit ? LANDED_MARK : ''
      }`}
    >
      <button
        type="button"
        className="flex w-full items-start justify-between gap-2 rounded-[var(--radius)] text-left focus-visible:outline-2 focus-visible:outline-offset-2"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-sm font-medium">
          <span className="text-muted-foreground">#{ticket.number}</span> {ticket.title}
        </span>
        <TicketStatusBadge status={ticket.status} />
      </button>
      {open ? (
        <div id={bodyId} className="space-y-2">
          <ThreadBody ticket={ticket} userId={userId} tz={tz} />
        </div>
      ) : null}
    </li>
  );
}

function ThreadBody({
  ticket,
  userId,
  tz,
}: {
  ticket: { id: string; number: number };
  userId: string;
  tz: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const detail = useQuery(trpc.ticket.get.queryOptions({ ticketId: ticket.id }));
  const [text, setText] = useDraft({ kind: 'ticket-message', userId, id: ticket.id }, '');
  const meta = { draftKey: draftKey('ticket-message', userId, ticket.id) };
  const unsent = useUnsentWrite('ticket.message', ticket.id, 'ticketId');
  const checking = !useQueueRead();
  /**
   * 🔴 One message per tap: the write goes out on the tap, so a double tap lands twice in the
   * same render's handler, each with its own key (LESSONS 2026-10-02). The lock is taken
   * before the key is minted (`sendOnce`) and released when the write settles: by then it has
   * landed (and the draft is empty) or failed. Queued offline, it holds until then, as the
   * button does (`unsent`).
   */
  const lock = useTapOnce();
  const message = useMutation(
    trpc.ticket.message.mutationOptions({
      meta,
      onSuccess: (_data, variables) => {
        void clearLandedDraft('ticket.message', variables, meta);
        return refreshUntilShown(qc, trpc.ticket.get.pathFilter());
      },
      onSettled: () => lock.release(),
    }),
  );
  const phase = mutationState(message);

  function send() {
    const body = text.trim();
    if (!body) return;
    sendOnce(lock, message.mutate, {
      ticketId: ticket.id,
      body,
      visibility: 'internal' as const,
    });
  }

  const messages = detail.data?.messages ?? [];
  return (
    <>
      {detail.isError ? (
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn’t load its messages.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void detail.refetch()}
          >
            Try again
          </button>
        </p>
      ) : messages.length > 0 ? (
        <div className="text-sm">
          <MessageList messages={messages} tz={tz} showDelivery={false} />
        </div>
      ) : null}
      <PhotoGrid entityType="ticket" entityId={ticket.id} compact />
      <textarea
        className="min-h-[60px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={4000}
        placeholder="Add a message for the office…"
        aria-label={`Message on ticket ${ticket.number}`}
      />
      <Button
        className="w-full"
        disabled={
          awaitingServer(message) || unsent || checking || text.trim().length === 0
        }
        onClick={send}
      >
        {phase === 'queued'
          ? 'Saved — sends when you’re back'
          : phase === 'sending'
            ? 'Sending…'
            : checking
              ? CHECKING_LABEL
              : unsent
                ? 'Saved — sends when you’re back'
                : 'Add a message'}
      </Button>
    </>
  );
}
